// classes/RangerClass.js
// Ranger: Versatile assault specialist, balanced for close-medium range combat

export const RangerClass = {
  id: 'ranger',
  name: 'Ranger',
  description: 'Versatile assault specialist, balanced for close-medium range combat',
  icon: '⚔️',
  recruitCost: 0, // Free - specialization is mandatory at Sergeant
  
  baseStats: {
    health: 100,
    maxHealth: 100,
    aim: 75,
    mobility: 80,
    defense: 60,
    willpower: 75,
    critChance: 15
  },

  skills: [
    // TIER 1 - Basic Training
    {
      id: 'combat_ready',
      name: 'Combat Ready',
      tier: 1,
      xpRequired: 0,
      description: 'Basic assault training. +10 Mobility',
      effect: { mobility: 10 }
    },
    {
      id: 'run_and_gun',
      name: 'Run and Gun',
      tier: 1,
      xpRequired: 25,
      description: 'Can fire weapon after dashing',
      effect: { dashShoot: true }
    },

    // TIER 2 - Specialist
    {
      id: 'tactical_sense',
      name: 'Tactical Sense',
      tier: 2,
      xpRequired: 60,
      description: 'Improved battlefield awareness. +10 Defense',
      effect: { defense: 10 }
    },
    {
      id: 'lightning_reflexes',
      name: 'Lightning Reflexes',
      tier: 2,
      xpRequired: 60,
      description: 'React faster in combat. First reaction shot each turn has +30 aim',
      effect: { firstReactionAimBonus: 30 }
    },

    // TIER 3 - Expert
    {
      id: 'rapid_fire',
      name: 'Rapid Fire',
      tier: 3,
      xpRequired: 125,
      description: 'Take two shots with -15 aim penalty on each',
      effect: { rapidFireEnabled: true, rapidFireAimPenalty: -15 }
    },
    {
      id: 'close_combat_specialist',
      name: 'Close Combat Specialist',
      tier: 3,
      xpRequired: 125,
      description: 'Bonus at close range. +15 Aim, +20% Crit within 8 tiles',
      effect: { closeRangeBonus: { aim: 15, critChance: 20, range: 8 } }
    },

    // TIER 4 - Veteran
    {
      id: 'aggression',
      name: 'Aggression',
      tier: 4,
      xpRequired: 200,
      description: 'Each kill grants +10 aim for the rest of the turn',
      effect: { killAimBonus: 10 }
    },
    {
      id: 'tactical_rigging',
      name: 'Tactical Rigging',
      tier: 4,
      xpRequired: 200,
      description: 'Can carry an extra grenade or utility item',
      effect: { extraUtilitySlot: true }
    },

    // TIER 5 - Elite
    {
      id: 'street_fighter',
      name: 'Street Fighter',
      tier: 5,
      xpRequired: 300,
      description: 'Melee attacks deal increased damage. +40% melee damage',
      effect: { meleeDamageBonus: 40 }
    },
    {
      id: 'resilience',
      name: 'Resilience',
      tier: 5,
      xpRequired: 300,
      description: 'Tougher than most. +20 Health, +10 Willpower',
      effect: { health: 20, maxHealth: 20, willpower: 10 }
    },

    // TIER 6 - Master
    {
      id: 'blademaster',
      name: 'Blademaster',
      tier: 6,
      xpRequired: 450,
      description: 'Melee attacks are guaranteed critical hits',
      effect: { meleeCritGuaranteed: true }
    },
    {
      id: 'untouchable',
      name: 'Untouchable',
      tier: 6,
      xpRequired: 450,
      description: 'First attack against you each turn is guaranteed to miss',
      effect: { firstAttackMiss: true }
    },

    // TIER 7 - Legendary
    {
      id: 'death_or_glory',
      name: 'Death or Glory',
      tier: 7,
      xpRequired: 625,
      description: 'Massive bonuses at low health. +30 Aim, +40% Crit when below 30% health',
      effect: { lowHealthBonus: { threshold: 30, aim: 30, critChance: 40 } }
    },
    {
      id: 'combat_rush',
      name: 'Combat Rush',
      tier: 7,
      xpRequired: 625,
      description: 'Killing an enemy refunds 1 action point',
      effect: { killRefundAP: 1 }
    },

    // TIER 8 - Mythic
    {
      id: 'reaper',
      name: 'Reaper',
      tier: 8,
      xpRequired: 875,
      description: 'Can fire after every kill (once per turn)',
      effect: { killChainShot: true }
    },
    {
      id: 'fortress',
      name: 'Fortress',
      tier: 8,
      xpRequired: 875,
      description: 'Become immovable. +30 Defense, immune to most status effects',
      effect: { defense: 30, statusImmune: true }
    },

    // TIER 9 - God Tier
    {
      id: 'war_machine',
      name: 'War Machine',
      tier: 9,
      xpRequired: 1250,
      description: 'The ultimate warrior. +25 to all stats, +3 action points per turn',
      effect: { 
        health: 25, maxHealth: 25, aim: 25, mobility: 25, 
        defense: 25, willpower: 25, bonusActionPoints: 3 
      }
    }
  ],

  abilities: [
    {
      id: 'suppression',
      name: 'Suppression',
      unlockAtXP: 0,
      cooldown: 2,
      description: 'Reduce enemy aim by 30 and prevent them from moving',
      usesPerMission: null
    },
    {
      id: 'flashbang',
      name: 'Flashbang',
      unlockAtXP: 75,
      cooldown: 3,
      description: 'Disorient enemies in an area, reducing aim and mobility',
      usesPerMission: 3
    },
    {
      id: 'blade_storm',
      name: 'Blade Storm',
      unlockAtXP: 250,
      cooldown: 4,
      description: 'Attack all adjacent enemies with your melee weapon',
      usesPerMission: 2
    },
    {
      id: 'serial_killer',
      name: 'Serial Killer',
      unlockAtXP: 500,
      cooldown: 5,
      description: 'Take unlimited shots until you miss',
      usesPerMission: 1
    },
    {
      id: 'devastating_blow',
      name: 'Devastating Blow',
      unlockAtXP: 1000,
      cooldown: 6,
      description: 'A melee attack that deals 300% damage and stuns',
      usesPerMission: 1
    }
  ]
};